import { ComponentProps, useMemo } from 'react'
import { ClipboardCopy } from '@components/ClipboardCopy/ClipboardCopy'
import { useCoinData } from '@components/TradingChart/useCoinData'
import { useQuery } from '@tanstack/react-query'
import { Box, Icon, Stack, Text } from '@ui'
import { MONTH_MS } from 'data/constants'
import { formatUnitsToFixedLength } from 'hooks/useBalance'
import { Accordion } from 'ui/components/Accordion/Accordion'
import { shortAddress } from 'ui/utils/utils'
import { type Address, formatEther, type Hex } from 'viem'
import * as chains from 'viem/chains'

import {
    decodeTransaction,
    getFunctionSelector,
    getTransactionTypeLabel,
    isUnlimitedApproval,
} from '../../../utils/transactionDecoder'

// Fetch function signature from 4byte.directory
const fetch4ByteSignature = async (selector: string): Promise<string | undefined> => {
    const response = await fetch(
        `https://www.4byte.directory/api/v1/signatures/?hex_signature=${selector}`,
    )
    if (!response.ok) {
        return undefined
    }
    const data = await response.json()
    if (data.results && data.results.length > 0) {
        return data.results[0].text_signature
    }
    return undefined
}

// Hook to lookup function signature from 4byte.directory
const use4ByteSignature = (selector: string | undefined) => {
    const { data: signature } = useQuery({
        queryKey: ['4byte-signature', selector],
        queryFn: () => fetch4ByteSignature(selector ?? ''),
        enabled: !!selector && selector.length === 10,
        staleTime: MONTH_MS,
        gcTime: MONTH_MS,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    })

    return signature
}

// Helper to get block explorer URL for an address
const getBlockExplorerUrl = (address: string, chainId: string): string => {
    const parsedChainId = parseInt(chainId)

    const explorers: Record<number, string> = {
        // Ethereum
        1: 'https://etherscan.io',
        5: 'https://goerli.etherscan.io', // Goerli
        11155111: 'https://sepolia.etherscan.io', // Sepolia

        // Base
        8453: 'https://basescan.org',
        84532: 'https://sepolia.basescan.org',

        // Arbitrum
        42161: 'https://arbiscan.io',
        421614: 'https://sepolia.arbiscan.io',

        // Optimism
        10: 'https://optimistic.etherscan.io',
        11155420: 'https://sepolia-optimism.etherscan.io',

        // Polygon
        137: 'https://polygonscan.com',
        80002: 'https://amoy.polygonscan.com', // Amoy testnet

        // BSC
        56: 'https://bscscan.com',
        97: 'https://testnet.bscscan.com',

        // Avalanche
        43114: 'https://snowtrace.io',
        43113: 'https://testnet.snowtrace.io',

        // Fantom
        250: 'https://ftmscan.com',
        4002: 'https://testnet.ftmscan.com',

        // Gnosis
        100: 'https://gnosisscan.io',
    }

    const baseUrl = explorers[parsedChainId] || 'https://etherscan.io'
    return `${baseUrl}/address/${address}`
}

// Component to display address with block explorer link
const AddressLink = ({
    address,
    chainId,
    label,
}: {
    address: string
    chainId: string
    label?: string
}) => {
    const url = getBlockExplorerUrl(address, chainId)
    return (
        <Box
            as="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: 'none', color: 'inherit' }}
        >
            <Row>
                {label && <RowTitle>{label}:</RowTitle>}
                <Stack horizontal gap="xxs" alignItems="center" height="x1.5">
                    <MonospaceText>{shortAddress(address)}</MonospaceText>
                    <Icon type="linkOut" size="square_xs" />
                </Stack>
            </Row>
        </Box>
    )
}

// Helper to get chain name from chain ID using viem's chain definitions
const getChainName = (chainId: string): string => {
    const parsedChainId = parseInt(chainId)

    // Find the chain in viem's chain definitions
    const chain = Object.values(chains).find((c) => c.id === parsedChainId)

    return chain?.name || `Chain ${chainId}`
}

// Helper to get chain icon type from chain ID
const getChainIconType = (chainId: string): 'base' | 'eth' | 'link' => {
    const parsedChainId = parseInt(chainId)

    // Map chain IDs to their icon types
    if (parsedChainId === 8453 || parsedChainId === 84532) {
        return 'base' // Base & Base Sepolia
    }
    if (
        parsedChainId === 1 ||
        parsedChainId === 5 ||
        parsedChainId === 11155111 ||
        parsedChainId === 17000
    ) {
        return 'eth' // Ethereum mainnet & testnets
    }

    return 'link' // Default for other chains
}

// Component to display chain info with icon
const ChainLink = ({ chainId }: { chainId: string }) => {
    const chainName = getChainName(chainId)
    const iconType = getChainIconType(chainId)

    return (
        <Stack horizontal gap="xs" alignItems="center" height="x1.5">
            <Icon type={iconType} size="square_xs" />
            <RowContent>{chainName}</RowContent>
            <RowContent>{chainId}</RowContent>
        </Stack>
    )
}

type EVMTransactionPreviewProps = {
    chainId: string
    to: string
    value?: string
    data?: string
}

export const EVMTransactionPreview = ({ chainId, to, value, data }: EVMTransactionPreviewProps) => {
    // Decode transaction data using useMemo
    const decoded = useMemo(() => {
        if (!data) {
            return null
        }
        return decodeTransaction(data as Hex, to as Address)
    }, [data, to])

    // Get function selector for unknown transactions
    const selector = useMemo(() => {
        if (decoded?.type === 'unknown' && data) {
            return getFunctionSelector(data as Hex)
        }
        return undefined
    }, [decoded?.type, data])

    // Lookup function signature from 4byte.directory for unknown transactions
    const fourByteSignature = use4ByteSignature(selector)

    // Use useCoinData for getting token metadata (includes logo, price, etc)
    const { data: coinData, isLoading } = useCoinData({
        address: to.toLowerCase(),
        chain: chainId,
        disabled: !to || !chainId || !decoded || decoded.type === 'unknown',
    })

    // Derive token metadata from coinData
    const tokenMetadata = useMemo(() => {
        if (coinData?.token) {
            return {
                address: to as Address,
                symbol: coinData.token.symbol,
                name: coinData.token.name,
                decimals: coinData.token.decimals,
                logoUrl: coinData.token.info.imageThumbUrl || undefined,
                chainId: parseInt(chainId),
            }
        }
        return null
    }, [coinData, to, chainId])

    // Format the transaction value in ETH
    const formattedEthValue = useMemo(() => {
        if (!value || value === '0' || value === '0x' || value === '0x0') {
            return null
        }
        try {
            const ethValue = formatEther(BigInt(value))
            // Hide if value is 0 ETH
            if (ethValue === '0.0') {
                return null
            }
            return `${ethValue} ETH`
        } catch {
            return `${value} wei`
        }
    }, [value])

    // Render transaction-specific preview content (without wrapper)
    const renderTransactionContent = () => {
        if (!decoded) {
            return (
                <>
                    <Text fontWeight="strong">Contract Interaction</Text>
                    <Text>Calling contract function</Text>
                </>
            )
        }

        switch (decoded.type) {
            case 'erc20-transfer': {
                const formattedAmount =
                    decoded.tokens[0]?.amount && tokenMetadata
                        ? formatUnitsToFixedLength(
                              decoded.tokens[0].amount,
                              tokenMetadata.decimals,
                              5,
                          )
                        : null

                return (
                    <>
                        <Stack horizontal gap="xs" alignItems="center">
                            <Icon type="swap" size="square_inline" color="default" />
                            <Text fontWeight="strong">
                                Send{' '}
                                {formattedAmount
                                    ? `${formattedAmount} ${tokenMetadata?.symbol || 'Tokens'}`
                                    : tokenMetadata?.symbol || 'Tokens'}
                            </Text>
                        </Stack>
                        {decoded.recipient && (
                            <AddressLink address={decoded.recipient} chainId={chainId} label="To" />
                        )}
                    </>
                )
            }

            case 'erc20-transfer-from': {
                const formattedAmount =
                    decoded.tokens[0]?.amount && tokenMetadata
                        ? formatUnitsToFixedLength(
                              decoded.tokens[0].amount,
                              tokenMetadata.decimals,
                              5,
                          )
                        : null

                return (
                    <>
                        <Stack horizontal gap="xs" alignItems="center">
                            <Icon type="swap" size="square_inline" color="default" />
                            <Text fontWeight="strong">
                                Transfer{' '}
                                {formattedAmount
                                    ? `${formattedAmount} ${tokenMetadata?.symbol || 'Tokens'}`
                                    : tokenMetadata?.symbol || 'Tokens'}
                            </Text>
                        </Stack>
                        {decoded.from && (
                            <AddressLink address={decoded.from} chainId={chainId} label="From" />
                        )}
                        {decoded.recipient && (
                            <AddressLink address={decoded.recipient} chainId={chainId} label="To" />
                        )}
                    </>
                )
            }

            case 'erc20-approve': {
                const amount = decoded.tokens[0]?.amount
                const isUnlimited = amount ? isUnlimitedApproval(amount) : false
                const formattedAmount =
                    amount && tokenMetadata && !isUnlimited
                        ? formatUnitsToFixedLength(amount, tokenMetadata.decimals, 5)
                        : null

                return (
                    <>
                        <Stack horizontal gap="xs" alignItems="center">
                            <Icon type="lock" size="square_inline" />
                            <Text fontWeight="strong">
                                {isUnlimited
                                    ? `Approve Unlimited ${tokenMetadata?.symbol || 'Token'}`
                                    : formattedAmount
                                      ? `Approve ${formattedAmount} ${tokenMetadata?.symbol || 'Token'}`
                                      : `Approve ${tokenMetadata?.symbol || 'Token'}`}
                            </Text>
                        </Stack>
                        {decoded.spender && (
                            <AddressLink
                                address={decoded.spender}
                                chainId={chainId}
                                label="Spender"
                            />
                        )}
                    </>
                )
            }

            case 'nft-transfer': {
                const isERC1155 = decoded.protocol === 'erc1155'
                const isBatch = decoded.tokens.length > 1

                // For batch transfers, show summary
                if (isBatch) {
                    const totalItems = decoded.tokens.reduce(
                        (sum, token) => sum + (token.amount ? Number(token.amount) : 1),
                        0,
                    )
                    return (
                        <>
                            <Stack horizontal gap="xs" alignItems="center">
                                <Icon type="image" size="square_inline" />
                                <Text fontWeight="strong">
                                    Batch Transfer {decoded.tokens.length}{' '}
                                    {tokenMetadata?.symbol || 'NFT'} types
                                    {totalItems > decoded.tokens.length &&
                                        ` (${totalItems} total items)`}
                                </Text>
                            </Stack>
                            {decoded.from && (
                                <AddressLink
                                    address={decoded.from}
                                    chainId={chainId}
                                    label="From"
                                />
                            )}
                            {decoded.recipient && (
                                <AddressLink
                                    address={decoded.recipient}
                                    chainId={chainId}
                                    label="To"
                                />
                            )}
                            {/* Show individual items */}
                            <Stack gap="xxs">
                                {decoded.tokens.slice(0, 5).map((token) => (
                                    <Text key={token.tokenId?.toString()} fontSize="sm">
                                        #{token.tokenId?.toString() || '?'}
                                        {token.amount && token.amount > 1n
                                            ? ` (${token.amount.toString()}x)`
                                            : ''}
                                    </Text>
                                ))}
                                {decoded.tokens.length > 5 && (
                                    <Text fontSize="sm">+{decoded.tokens.length - 5} more...</Text>
                                )}
                            </Stack>
                        </>
                    )
                }

                // Single transfer
                const tokenId = decoded.tokens[0]?.tokenId
                const amount = decoded.tokens[0]?.amount

                return (
                    <>
                        <Stack horizontal gap="xs" alignItems="center">
                            <Icon type="image" size="square_inline" />
                            <Text fontWeight="strong">
                                Transfer {tokenMetadata?.symbol || 'NFT'}
                                {tokenId && ` #${tokenId.toString()}`}
                                {isERC1155 && amount && ` (${amount.toString()}x)`}
                            </Text>
                        </Stack>
                        {decoded.from && (
                            <AddressLink address={decoded.from} chainId={chainId} label="From" />
                        )}
                        {decoded.recipient && (
                            <AddressLink address={decoded.recipient} chainId={chainId} label="To" />
                        )}
                    </>
                )
            }

            case 'nft-set-approval-all':
                return (
                    <>
                        <Stack horizontal gap="xs" alignItems="center">
                            <Icon type="lock" size="square_inline" />
                            <Text fontWeight="strong">
                                {decoded.description || 'Set Approval For All'}
                            </Text>
                        </Stack>
                        <AddressLink
                            address={to}
                            chainId={chainId}
                            label={
                                tokenMetadata?.name
                                    ? `Collection: ${tokenMetadata.name}`
                                    : 'Collection'
                            }
                        />
                        {decoded.spender && (
                            <AddressLink
                                address={decoded.spender}
                                chainId={chainId}
                                label="Operator"
                            />
                        )}
                    </>
                )

            default:
                return (
                    <>
                        <RowTitle>{getTransactionTypeLabel(decoded.type)}</RowTitle>
                        {fourByteSignature ? (
                            <MonospaceText wordBreak="break-word">
                                {fourByteSignature}
                            </MonospaceText>
                        ) : (
                            decoded.description && <Text>{decoded.description}</Text>
                        )}
                    </>
                )
        }
    }

    return (
        <Stack gap="sm">
            {/* Main transaction preview */}
            <Stack border padding="x1.5" gap="xs" borderRadius="sm">
                {/* Transaction details - main content */}
                {isLoading ? (
                    <Text>Loading token details...</Text>
                ) : (
                    <Stack gap="x1.5">
                        {/* Transaction preview - wrapped in consistent Stack */}
                        <Row>{renderTransactionContent()}</Row>

                        {/* Metadata grid */}
                        <Stack gap="x1.5">
                            {/* Chain and Contract row */}
                            <Stack horizontal gap="lg">
                                <Row minWidth="100">
                                    <RowTitle>Chain</RowTitle>
                                    <ChainLink chainId={chainId} />
                                </Row>
                                <Row grow>
                                    <RowTitle>Contract</RowTitle>
                                    <Stack horizontal gap="xs" height="x1.5">
                                        {tokenMetadata?.logoUrl && (
                                            <Box
                                                as="img"
                                                src={tokenMetadata.logoUrl}
                                                square="square_sm"
                                                style={{ borderRadius: '50%' }}
                                            />
                                        )}
                                        <AddressLink
                                            address={to}
                                            chainId={chainId}
                                            label={tokenMetadata?.name}
                                        />
                                    </Stack>
                                </Row>
                            </Stack>

                            {/* Function signature */}
                            {(decoded?.functionSignature || fourByteSignature) && (
                                <Row>
                                    <RowTitle>Function</RowTitle>
                                    <MonospaceText wordBreak="break-word">
                                        {decoded?.functionSignature || fourByteSignature}
                                    </MonospaceText>
                                </Row>
                            )}

                            {/* ETH value if present */}
                            {formattedEthValue && (
                                <Row>
                                    <RowTitle>Value</RowTitle>
                                    <RowContent>{formattedEthValue}</RowContent>
                                </Row>
                            )}

                            {/* Additional data parameter if present */}
                            {decoded?.data && decoded.data !== '0x' && (
                                <Stack gap="xs">
                                    <Text fontWeight="strong">Data</Text>
                                    <Text
                                        fontWeight="medium"
                                        style={{
                                            fontFamily: 'monospace',
                                            wordBreak: 'break-all',
                                            fontSize: '12px',
                                        }}
                                    >
                                        {decoded.data}
                                    </Text>
                                </Stack>
                            )}

                            {/* Warnings - displayed after other metadata */}
                            {decoded?.warnings && decoded.warnings.length > 0 && (
                                <Stack gap="xs" padding="xs" background="level2" borderRadius="xs">
                                    {decoded.warnings.map((warning: string) => (
                                        <Stack
                                            horizontal
                                            key={warning}
                                            gap="xs"
                                            alignItems="center"
                                        >
                                            <Icon
                                                type="warning"
                                                size="square_inline"
                                                color="negative"
                                            />
                                            <Text color="negative">{warning}</Text>
                                        </Stack>
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                )}
            </Stack>

            {/* Raw data toggle */}
            {data && data !== '0x' && (
                <Accordion
                    gap="none"
                    header={({ isExpanded }: { isExpanded: boolean }) => (
                        <Stack
                            horizontal
                            padding="x1.5"
                            gap="xs"
                            alignItems="center"
                            justifyContent="spaceBetween"
                            border={!isExpanded}
                            background="level2"
                            rounded="sm"
                            borderBottomLeftRadius={isExpanded ? 'none' : 'sm'}
                            borderBottomRightRadius={isExpanded ? 'none' : 'sm'}
                        >
                            <Text fontSize="sm" color="gray1">
                                {isExpanded ? 'Hide' : 'Show'} raw transaction data
                            </Text>
                            <Icon type={isExpanded ? 'eyeOff' : 'eye'} size="square_xxs" />
                        </Stack>
                    )}
                >
                    <Stack border paddingX="x1.5" paddingY="x2" gap="paragraph" background="level1">
                        <ClipboardCopy
                            clipboardContent={data}
                            fontSize="sm"
                            color="default"
                            label="Raw Call Data"
                        />
                        <Text
                            fontSize="xs"
                            style={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
                        >
                            {data}
                        </Text>
                    </Stack>
                </Accordion>
            )}
        </Stack>
    )
}

const Row = (props: ComponentProps<typeof Stack>) => <Stack gap="x1" {...props} />

const RowTitle = (props: ComponentProps<typeof Text>) => (
    <Text fontSize="sm" color="gray2" {...props} />
)

const RowContent = (props: ComponentProps<typeof Text>) => (
    <Text fontSize="sm" color="gray1" {...props} />
)

const MonospaceText = (props: ComponentProps<typeof Text>) => (
    <Text color="gray1" style={{ fontFamily: 'monospace', fontSize: '0.8rem' }} {...props} />
)
